"use client"

import * as React from "react"
import {
  FolderOpen,
  Plus,
  Settings,
  Archive,
  ArchiveRestore,
  X,
  Pencil,
  AlertTriangle,
  Loader2,
  CheckCircle2,
  Layers,
  Inbox
} from "lucide-react"
import { TabsContent } from "@/components/ui/tabs"
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Account, UserProfile, Workspace } from "@/types/workspace"
import { WorkspaceMembersManager } from "@/components/settings/tabs/WorkspaceMembersManager"
import { toast } from "@/lib/toast"

export interface WorkspacesSettingsTabProps {
  currentAccount: Account | null
  handleCreateWorkspace: (name: string, accountId: string) => Promise<any>
  handleUpdateWorkspace?: (id: string, name: string) => Promise<any>
  handleArchiveWorkspace?: (id: string) => Promise<any>
  handleReactivateWorkspace?: (id: string) => Promise<any>
  handleDeleteWorkspace?: (id: string) => Promise<any>
  usersList: UserProfile[]
  t: Record<string, string>
  profileRole?: string
  user?: UserProfile | null
}

export function WorkspacesSettingsTab({
  currentAccount,
  handleCreateWorkspace,
  handleUpdateWorkspace,
  handleArchiveWorkspace,
  handleReactivateWorkspace,
  handleDeleteWorkspace,
  usersList,
  t,
  profileRole = "member",
  user,
}: WorkspacesSettingsTabProps) {
  const isCompanyAdmin = profileRole === "owner" || profileRole === "admin"
  const [filterTab, setFilterTab] = React.useState<"all" | "active" | "archived">("active")
  const [newWsName, setNewWsName] = React.useState("")
  const [showCreateWorkspaceModal, setShowCreateWorkspaceModal] = React.useState(false)
  const [isSubmittingWorkspace, setIsSubmittingWorkspace] = React.useState(false)
  const [editingWorkspaceModal, setEditingWorkspaceModal] = React.useState<Workspace | null>(null)
  const [editWorkspaceNameInput, setEditWorkspaceNameInput] = React.useState("")
  const [userWorkspaceRoles, setUserWorkspaceRoles] = React.useState<Record<string, string>>({})
  const [workspaceToArchive, setWorkspaceToArchive] = React.useState<Workspace | null>(null)
  const [isArchivingWorkspace, setIsArchivingWorkspace] = React.useState(false)
  const [workspaceToReactivate, setWorkspaceToReactivate] = React.useState<Workspace | null>(null)
  const [isReactivatingWorkspace, setIsReactivatingWorkspace] = React.useState(false)

  // Resolvers para arquivar e reativar (com fallback para handleDeleteWorkspace se handleArchiveWorkspace não for passado)
  const archiveAction = handleArchiveWorkspace || handleDeleteWorkspace
  const reactivateAction = handleReactivateWorkspace

  // Buscar papéis do usuário nos workspaces caso ele seja membro comum da empresa
  React.useEffect(() => {
    if (isCompanyAdmin) return
    const token = document.cookie
      .split("; ")
      .find((row) => row.startsWith("token="))
      ?.split("=")[1]
    const userId = user?.id || (user as any)?.userId
    if (!token || !userId) return

    fetch("/api/graphql", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({
        query: `
          query GetUserWorkspaceMemberships($userId: ID!) {
            userWorkspaceMemberships(userId: $userId) {
              workspaceId
              role
            }
          }
        `,
        variables: { userId }
      })
    })
      .then((res) => res.json())
      .then((res) => {
        if (res.data?.userWorkspaceMemberships) {
          const map: Record<string, string> = {}
          res.data.userWorkspaceMemberships.forEach((m: any) => {
            map[m.workspaceId] = m.role
          })
          setUserWorkspaceRoles(map)
        }
      })
      .catch((e) => console.error("Erro ao buscar papéis nos workspaces:", e))
  }, [user?.id, (user as any)?.userId, isCompanyAdmin])

  const allWorkspaces = currentAccount?.workspaces || []
  const activeWorkspaces = allWorkspaces.filter((w) => w.status !== false)
  const archivedWorkspaces = allWorkspaces.filter((w) => w.status === false)
  const canArchiveMore = activeWorkspaces.length > 1

  const displayedWorkspaces = filterTab === "active"
    ? activeWorkspaces
    : filterTab === "archived"
      ? archivedWorkspaces
      : allWorkspaces

  return (
    <TabsContent value="workspaces" className="outline-none space-y-6">
      {/* CABEÇALHO */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h3 className="text-lg font-bold text-zinc-900 dark:text-white flex items-center gap-2">
            <FolderOpen className="size-5 text-[#004EB1]" />
            Gerenciar Workspaces
          </h3>
          <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-1 font-sans">
            {isCompanyAdmin
              ? "Crie, arquive e gerencie os espaços de trabalho da sua empresa, configure membros e permissões."
              : "Visualize e gerencie os espaços de trabalho aos quais você tem acesso nesta empresa."}
          </p>
        </div>

        {isCompanyAdmin && (
          <Button
            type="button"
            onClick={() => {
              setNewWsName("")
              setShowCreateWorkspaceModal(true)
            }}
            className="bg-[#004EB1] hover:bg-[#003d8d] text-white text-xs font-semibold h-9 px-4 gap-1.5 cursor-pointer shadow-sm shrink-0"
          >
            <Plus className="size-4" />
            Criar Workspace
          </Button>
        )}
      </div>

      {/* FILTRO DE ABAS (TODOS / ATIVOS / ARQUIVADOS) */}
      {isCompanyAdmin && (
        <div className="flex items-center gap-1.5 border-b border-zinc-200 dark:border-zinc-800 pb-2">
          <button
            type="button"
            onClick={() => setFilterTab("all")}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center gap-2 cursor-pointer ${filterTab === "all"
              ? "bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 shadow-xs"
              : "text-zinc-500 hover:text-zinc-900 dark:hover:text-white hover:bg-zinc-100 dark:hover:bg-zinc-800"
              }`}
          >
            <Layers className="size-3.5" />
            Todos
            <span className={`text-[10px] px-1.5 py-0.2 rounded-full ${filterTab === "all"
              ? "bg-white/20 dark:bg-black/20 text-white dark:text-zinc-900"
              : "bg-zinc-100 dark:bg-zinc-800 text-zinc-500"
              }`}>
              {allWorkspaces.length}
            </span>
          </button>

          <button
            type="button"
            onClick={() => setFilterTab("active")}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center gap-2 cursor-pointer ${filterTab === "active"
              ? "bg-emerald-600 text-white shadow-xs"
              : "text-zinc-500 hover:text-zinc-900 dark:hover:text-white hover:bg-zinc-100 dark:hover:bg-zinc-800"
              }`}
          >
            <span className="size-2 rounded-full bg-emerald-400"></span>
            Ativos
            <span className={`text-[10px] px-1.5 py-0.2 rounded-full ${filterTab === "active"
              ? "bg-white/20 text-white"
              : "bg-zinc-100 dark:bg-zinc-800 text-zinc-500"
              }`}>
              {activeWorkspaces.length}
            </span>
          </button>

          <button
            type="button"
            onClick={() => setFilterTab("archived")}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center gap-2 cursor-pointer ${filterTab === "archived"
              ? "bg-amber-600 text-white shadow-xs"
              : "text-zinc-500 hover:text-zinc-900 dark:hover:text-white hover:bg-zinc-100 dark:hover:bg-zinc-800"
              }`}
          >
            <Archive className="size-3.5" />
            Arquivados
            <span className={`text-[10px] px-1.5 py-0.2 rounded-full ${filterTab === "archived"
              ? "bg-white/20 text-white"
              : "bg-zinc-100 dark:bg-zinc-800 text-zinc-500"
              }`}>
              {archivedWorkspaces.length}
            </span>
          </button>
        </div>
      )}

      {/* ESTADO VAZIO QUANDO FILTRADO */}
      {displayedWorkspaces.length === 0 ? (
        <div className="flex flex-col items-center justify-center p-12 text-center border border-dashed border-zinc-200 dark:border-zinc-800 rounded-2xl bg-zinc-50/50 dark:bg-zinc-900/30">
          <div className="size-12 rounded-2xl bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center text-zinc-400 mb-3">
            <Inbox className="size-6" />
          </div>
          <h4 className="text-sm font-bold text-zinc-700 dark:text-zinc-300">
            {filterTab === "archived" ? "Nenhum workspace arquivado" : "Nenhum workspace encontrado"}
          </h4>
          <p className="text-xs text-zinc-400 mt-1 max-w-sm">
            {filterTab === "archived"
              ? "Workspaces arquivados serão exibidos nesta seção quando você os arquivar."
              : "Não há workspaces disponíveis com os filtros selecionados."}
          </p>
        </div>
      ) : (
        /* LISTA DE WORKSPACES EM CARDS */
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {displayedWorkspaces.map((ws) => {
            const isWorkspaceAdmin = isCompanyAdmin || userWorkspaceRoles[ws.id] === "admin"
            const isArchived = ws.status === false

            return (
              <Card
                key={ws.id}
                className={`border transition-all shadow-xs ${isArchived
                  ? "border-amber-200/60 dark:border-amber-900/30 bg-amber-50/20 dark:bg-amber-950/10 opacity-85 hover:opacity-100"
                  : "border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 hover:border-zinc-300 dark:hover:border-zinc-700"
                  }`}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div
                        className={`size-10 rounded-xl flex items-center justify-center font-bold text-base border ${isArchived
                          ? "bg-zinc-100 dark:bg-zinc-800 text-zinc-500 border-zinc-200 dark:border-zinc-700"
                          : "bg-blue-50 dark:bg-blue-950/40 text-[#004EB1] dark:text-blue-400 border-blue-100 dark:border-blue-900/50"
                          }`}
                      >
                        {ws.name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <CardTitle className="text-sm font-bold text-zinc-900 dark:text-white">
                            {ws.name}
                          </CardTitle>
                          {isArchived ? (
                            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-md bg-amber-100 dark:bg-amber-950/60 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-900/50 flex items-center gap-1">
                              <Archive className="size-3" />
                              Arquivado
                            </span>
                          ) : (
                            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-md bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400 border border-emerald-100 dark:border-emerald-900/50 flex items-center gap-1">
                              <span className="size-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                              Ativo
                            </span>
                          )}
                        </div>
                        <CardDescription className="text-[11px] text-zinc-400 font-mono mt-0.5">
                          {ws.slug}
                        </CardDescription>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="flex items-center justify-between pt-3 border-t border-zinc-100 dark:border-zinc-800 mt-2">
                    <div className="text-[11px] text-zinc-400 font-medium">
                      {isArchived ? "Espaço Arquivado" : "Workspace Corporativo"}
                    </div>

                    {isWorkspaceAdmin ? (
                      <div className="flex items-center gap-2">
                        {!isArchived && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setEditingWorkspaceModal(ws)
                              setEditWorkspaceNameInput(ws.name)
                            }}
                            className="h-8 text-xs font-semibold text-[#004EB1] border-blue-200 dark:border-blue-900 hover:bg-blue-50 dark:hover:bg-blue-950/30 gap-1.5 cursor-pointer"
                          >
                            <Settings className="size-3.5" />
                            Configurar
                          </Button>
                        )}

                        {isCompanyAdmin && (
                          <>
                            {isArchived ? (
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => setWorkspaceToReactivate(ws)}
                                className="h-8 text-xs font-semibold text-emerald-600 dark:text-emerald-400 border-emerald-200 dark:border-emerald-900 hover:bg-emerald-50 dark:hover:bg-emerald-950/30 gap-1.5 cursor-pointer"
                                title="Reativar Workspace"
                              >
                                <ArchiveRestore className="size-3.5" />
                              </Button>
                            ) : (
                              archiveAction && (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  disabled={!canArchiveMore}
                                  onClick={() => setWorkspaceToArchive(ws)}
                                  className={`h-8 px-2.5 gap-1.5 text-xs font-semibold transition-colors cursor-pointer ${canArchiveMore
                                    ? "text-amber-600 hover:text-amber-700 hover:bg-amber-50 dark:hover:bg-amber-950/30"
                                    : "text-zinc-300 dark:text-zinc-600 cursor-not-allowed"
                                    }`}
                                  title={
                                    canArchiveMore
                                      ? "Arquivar Workspace"
                                      : "A empresa deve possuir pelo menos um workspace ativo."
                                  }
                                >
                                  <Archive className="size-3.5" />
                                </Button>
                              )
                            )}
                          </>
                        )}
                      </div>
                    ) : (
                      <span className="text-[10px] font-semibold text-zinc-500 dark:text-zinc-400 bg-zinc-100 dark:bg-zinc-800 px-2 py-0.5 rounded-md">
                        Acesso Concedido
                      </span>
                    )}
                  </div>
                </CardContent>
              </Card>
            )
          })}
        </div>
      )}

      {/* MODAL DE CRIAÇÃO DE WORKSPACE */}
      {showCreateWorkspaceModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-xs p-4">
          <div className="w-full max-w-md bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-2xl shadow-2xl overflow-hidden font-sans animate-in fade-in zoom-in-95 duration-150">
            <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-100 dark:border-zinc-800 bg-zinc-50/50 dark:bg-zinc-900/50">
              <div className="flex items-center gap-2">
                <div className="size-8 rounded-lg bg-blue-50 dark:bg-blue-950/50 text-[#004EB1] flex items-center justify-center">
                  <Plus className="size-4" />
                </div>
                <div>
                  <h4 className="text-sm font-bold text-zinc-900 dark:text-white">Criar Novo Workspace</h4>
                  <p className="text-[11px] text-zinc-500">Informe o nome do seu novo espaço de trabalho</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowCreateWorkspaceModal(false)}
                className="text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200 cursor-pointer"
              >
                <X className="size-4" />
              </button>
            </div>

            <form
              onSubmit={async (e) => {
                e.preventDefault()
                if (!newWsName.trim() || !currentAccount) return
                setIsSubmittingWorkspace(true)
                try {
                  const newWs = await handleCreateWorkspace(newWsName.trim(), currentAccount.id)
                  if (currentAccount) {
                    currentAccount.workspaces = [...(currentAccount.workspaces || []), newWs]
                  }
                  toast.success(`Workspace "${newWs.name}" criado com sucesso!`)
                  setNewWsName("")
                  setShowCreateWorkspaceModal(false)
                } catch (err: any) {
                  toast.error(err.message || "Erro ao criar workspace.")
                } finally {
                  setIsSubmittingWorkspace(false)
                }
              }}
              className="p-6 space-y-4"
            >
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-zinc-700 dark:text-zinc-300">
                  Nome do Workspace <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={newWsName}
                  onChange={(e) => setNewWsName(e.target.value)}
                  placeholder="Ex: Vendas, Marketing, Filial SP"
                  className="w-full rounded-lg border border-zinc-200 dark:border-zinc-800 px-3.5 py-2 text-xs bg-white dark:bg-zinc-950 text-zinc-900 dark:text-white focus:border-[#004EB1] focus:ring-1 focus:ring-[#004EB1] outline-none"
                />
                <p className="text-[10px] text-zinc-400">
                  O identificador slug será gerado automaticamente. Se já existir, receberá um sufixo numérico (_1, _2...).
                </p>
              </div>

              <div className="flex items-center justify-end gap-2 pt-2 border-t border-zinc-100 dark:border-zinc-800">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setShowCreateWorkspaceModal(false)}
                  className="text-xs h-9 px-4 cursor-pointer"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={!newWsName.trim() || isSubmittingWorkspace}
                  className="bg-[#004EB1] hover:bg-[#003d8d] text-white text-xs font-semibold h-9 px-5 cursor-pointer"
                >
                  {isSubmittingWorkspace ? "Criando..." : "Criar Workspace"}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL DE CONFIGURAÇÃO / EDIÇÃO DO WORKSPACE */}
      {editingWorkspaceModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-xs p-4 overflow-y-auto">
          <div className="w-full max-w-2xl bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-2xl shadow-2xl overflow-hidden font-sans my-8 animate-in fade-in zoom-in-95 duration-150">
            <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-100 dark:border-zinc-800 bg-zinc-50/50 dark:bg-zinc-900/50">
              <div className="flex items-center gap-3">
                <div className="size-9 rounded-xl bg-blue-50 dark:bg-blue-950/50 text-[#004EB1] flex items-center justify-center font-bold">
                  {editingWorkspaceModal.name.charAt(0).toUpperCase()}
                </div>
                <div>
                  <h4 className="text-sm font-bold text-zinc-900 dark:text-white">
                    Configurar Workspace
                  </h4>
                  <p className="text-[11px] text-zinc-500 font-mono">
                    {editingWorkspaceModal.slug}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setEditingWorkspaceModal(null)}
                className="text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200 cursor-pointer"
              >
                <X className="size-4" />
              </button>
            </div>

            <div className="p-6 space-y-6 max-h-[80vh] overflow-y-auto">
              {/* Seção 1: Alterar Nome */}
              <div className="p-4 bg-zinc-50 dark:bg-zinc-950 border border-zinc-200 dark:border-zinc-800 rounded-xl space-y-3">
                <h5 className="text-xs font-bold text-zinc-900 dark:text-white uppercase tracking-wider flex items-center gap-2">
                  <Pencil className="size-3.5 text-[#004EB1]" />
                  Nome de Exibição
                </h5>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={editWorkspaceNameInput}
                    onChange={(e) => setEditWorkspaceNameInput(e.target.value)}
                    placeholder="Nome do Workspace"
                    className="flex-1 rounded-lg border border-zinc-200 dark:border-zinc-800 px-3.5 py-2 text-xs bg-white dark:bg-zinc-900 text-zinc-900 dark:text-white focus:border-[#004EB1] outline-none"
                  />
                  <Button
                    type="button"
                    disabled={!editWorkspaceNameInput.trim() || editWorkspaceNameInput === editingWorkspaceModal.name}
                    onClick={async () => {
                      if (!handleUpdateWorkspace || !editingWorkspaceModal) return
                      try {
                        const updated = await handleUpdateWorkspace(editingWorkspaceModal.id, editWorkspaceNameInput.trim())
                        if (currentAccount) {
                          currentAccount.workspaces = (currentAccount.workspaces || []).map((w) =>
                            w.id === updated.id ? { ...w, name: updated.name } : w
                          )
                        }
                        setEditingWorkspaceModal({ ...editingWorkspaceModal, name: updated.name })
                        toast.success("Nome do workspace atualizado!")
                      } catch (err: any) {
                        toast.error(err.message || "Erro ao atualizar nome.")
                      }
                    }}
                    className="bg-[#004EB1] hover:bg-[#003d8d] text-white text-xs h-9 px-4 cursor-pointer"
                  >
                    Salvar
                  </Button>
                </div>
              </div>

              {/* Seção 2: Membros e Perfis do Workspace */}
              <div className="space-y-3">
                <WorkspaceMembersManager
                  workspaceId={editingWorkspaceModal.id}
                  workspaceName={editingWorkspaceModal.name}
                  allUsers={usersList}
                />
              </div>
            </div>

            <div className="px-6 py-3 border-t border-zinc-100 dark:border-zinc-800 bg-zinc-50/50 dark:bg-zinc-900/50 flex justify-end">
              <Button
                type="button"
                onClick={() => setEditingWorkspaceModal(null)}
                className="bg-zinc-800 hover:bg-zinc-700 text-white text-xs font-semibold h-8 px-4 cursor-pointer"
              >
                Concluir
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL DE CONFIRMAÇÃO DE ARQUIVAMENTO DE WORKSPACE */}
      {workspaceToArchive && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center z-[999] animate-in fade-in duration-200 p-4 font-sans">
          <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-2xl w-full max-w-sm overflow-hidden shadow-2xl animate-in zoom-in-95 duration-200 p-5 space-y-4">
            <div className="size-11 rounded-full bg-amber-100 dark:bg-amber-950/50 text-amber-600 dark:text-amber-400 flex items-center justify-center mx-auto shadow-xs">
              <Archive className="size-5" />
            </div>

            <div className="text-center space-y-1.5">
              <h3 className="text-sm font-bold text-zinc-900 dark:text-white">
                Arquivar Workspace
              </h3>
              <p className="text-xs text-zinc-500 dark:text-zinc-400 leading-relaxed">
                Tem certeza de que deseja arquivar o workspace <strong>{workspaceToArchive.name}</strong>?
              </p>
              <div className="text-[11px] text-zinc-400 bg-zinc-50 dark:bg-zinc-800/50 p-2.5 rounded-xl text-left space-y-1 border border-zinc-100 dark:border-zinc-800">
                <p className="flex items-center gap-1.5 text-zinc-600 dark:text-zinc-300 font-medium">
                  <AlertTriangle className="size-3 text-amber-500 shrink-0" />
                  Os membros e preferências de layout serão desvinculados.
                </p>
                <p className="flex items-center gap-1.5 text-zinc-600 dark:text-zinc-300 font-medium">
                  <CheckCircle2 className="size-3 text-emerald-500 shrink-0" />
                  Aplicativos e widgets instalados serão preservados.
                </p>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 pt-2 border-t border-zinc-150 dark:border-zinc-800">
              <Button
                type="button"
                variant="ghost"
                disabled={isArchivingWorkspace}
                onClick={() => setWorkspaceToArchive(null)}
                className="text-xs h-8 font-sans cursor-pointer flex-1"
              >
                Cancelar
              </Button>
              <Button
                type="button"
                disabled={isArchivingWorkspace}
                onClick={async () => {
                  if (!archiveAction || !workspaceToArchive) return
                  setIsArchivingWorkspace(true)
                  try {
                    await archiveAction(workspaceToArchive.id)
                    toast.success(`Workspace "${workspaceToArchive.name}" arquivado com sucesso.`)
                    setWorkspaceToArchive(null)
                  } catch (err: any) {
                    toast.error(err.message || "Erro ao arquivar workspace.")
                  } finally {
                    setIsArchivingWorkspace(false)
                  }
                }}
                className="bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold h-8 px-4 gap-1.5 cursor-pointer flex-1 shadow-xs"
              >
                {isArchivingWorkspace ? (
                  <>
                    <Loader2 className="size-3.5 animate-spin" />
                    Arquivando...
                  </>
                ) : (
                  <>
                    <Archive className="size-3.5" />
                    Arquivar
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL DE CONFIRMAÇÃO DE REATIVAÇÃO DE WORKSPACE */}
      {workspaceToReactivate && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center z-[999] animate-in fade-in duration-200 p-4 font-sans">
          <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-2xl w-full max-w-sm overflow-hidden shadow-2xl animate-in zoom-in-95 duration-200 p-5 space-y-4">
            <div className="size-11 rounded-full bg-emerald-100 dark:bg-emerald-950/50 text-emerald-600 dark:text-emerald-400 flex items-center justify-center mx-auto shadow-xs">
              <ArchiveRestore className="size-5" />
            </div>

            <div className="text-center space-y-1.5">
              <h3 className="text-sm font-bold text-zinc-900 dark:text-white">
                Reativar Workspace
              </h3>
              <p className="text-xs text-zinc-500 dark:text-zinc-400 leading-relaxed">
                Deseja reativar o workspace <strong>{workspaceToReactivate.name}</strong>?
              </p>
              <div className="text-[11px] text-zinc-400 bg-zinc-50 dark:bg-zinc-800/50 p-2.5 rounded-xl text-left space-y-1 border border-zinc-100 dark:border-zinc-800">
                <p className="flex items-center gap-1.5 text-zinc-600 dark:text-zinc-300 font-medium">
                  <CheckCircle2 className="size-3 text-emerald-500 shrink-0" />
                  O acesso será restaurado para administradores e proprietário.
                </p>
                <p className="flex items-center gap-1.5 text-zinc-600 dark:text-zinc-300 font-medium">
                  <CheckCircle2 className="size-3 text-emerald-500 shrink-0" />
                  Todos os apps e widgets instalados estarão disponíveis.
                </p>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 pt-2 border-t border-zinc-150 dark:border-zinc-800">
              <Button
                type="button"
                variant="ghost"
                disabled={isReactivatingWorkspace}
                onClick={() => setWorkspaceToReactivate(null)}
                className="text-xs h-8 font-sans cursor-pointer flex-1"
              >
                Cancelar
              </Button>
              <Button
                type="button"
                disabled={isReactivatingWorkspace}
                onClick={async () => {
                  if (!reactivateAction || !workspaceToReactivate) return
                  setIsReactivatingWorkspace(true)
                  try {
                    await reactivateAction(workspaceToReactivate.id)
                    toast.success(`Workspace "${workspaceToReactivate.name}" reativado com sucesso!`)
                    setWorkspaceToReactivate(null)
                  } catch (err: any) {
                    toast.error(err.message || "Erro ao reativar workspace.")
                  } finally {
                    setIsReactivatingWorkspace(false)
                  }
                }}
                className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold h-8 px-4 gap-1.5 cursor-pointer flex-1 shadow-xs"
              >
                {isReactivatingWorkspace ? (
                  <>
                    <Loader2 className="size-3.5 animate-spin" />
                    Reativando...
                  </>
                ) : (
                  <>
                    <ArchiveRestore className="size-3.5" />
                    Reativar
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </TabsContent>
  )
}
